import {
  CreateButton,
  DeleteButton,
  EditButton,
  List,
  SaveButton,
} from '@refinedev/antd';
import { Button, Form, Input, Select, Space, Table } from 'antd';
import {
  IResourceComponentsProps,
  useGetIdentity,
  useNavigation,
  useTranslate,
} from '@refinedev/core';

import { User, UserRole } from '@cc/schema';
import { useCcEditableTable } from '../../helpers';
import { IUser } from '../../providers';

const metaData = {
  fields: [
    {
      data: ['_id', '_key', 'firstName', 'lastName', 'role', 'email'],
    },
    'count',
  ],
};

export const UserList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();

  const { push } = useNavigation();

  const { data: identity, remove, refetch } = useGetIdentity<IUser>();

  const {
    table: { tableProps },

    form: { formProps },
    isEditing,
    saveButtonProps,
    cancelButtonProps,
    editButtonProps,
  } = useCcEditableTable<User>(
    {
      queryOptions: {
        select: (data) => {
          // @ts-ignore
          return { data: data.data.data, total: data.total };
        },
      },
      /**
       * M7 · THE ROLES.5 DEFECT, server half (founder Q18, 2026-09-13).
       *
       * `practiceUsers` filters on `practiceId` and NOTHING ELSE, and `createNonAppClient` writes a
       * client row carrying the practice's own `practiceId` with role `NON_MOBILE` (5). So every
       * non-app client a practice ever created has been listed on this page — the TEAM roster — as
       * a member of staff, with the literal string "roles.5" in the role column.
       *
       * The rendering was the visible half; this is the dangerous half. The inline role editor
       * below offers exactly two options, THERAPIST and THERAPIST_ADMIN, so an admin who opened one
       * of those rows and pressed Save turned a CLIENT into a practitioner — silently, and with the
       * access that comes with it. A missing label is a blemish; a privilege escalation reachable
       * by a mis-click is not.
       *
       * WHY A RANGE AND NOT `role_in: [2, 3]`. The portal `where` builder interpolates an `in`
       * value straight into AQL (`d.role IN ${value}`), so an array arrives as `2,3` and the query
       * is malformed. `gte` / `lte` are the simple operators, are emitted as real comparisons, and
       * over a contiguous enum express exactly the same set — practitioner roles, 2 and 3.
       * `permanent` rather than `initial` so no column filter can clear it.
       */
      filters: {
        permanent: [
          { field: 'role', operator: 'gte', value: UserRole.THERAPIST },
          { field: 'role', operator: 'lte', value: UserRole.THERAPIST_ADMIN },
        ],
      },
      sorters: {
        initial: [
          {
            field: '_id',
            order: 'asc',
          },
        ],
      },
      meta: metaData,
    },
    {
      onMutationSuccess: () => {
        cancelButtonProps.onClick?.();
      },
      meta: {
        fields: ['_id', '_key', 'firstName', 'lastName', 'role', 'email'],
      },
    },
  );

  return (
    <List
      title={translate('practiceUsers.practiceUsers')}
      headerProps={{
        extra: (
          <Space>
            <CreateButton
              onClick={() => {
                push('/invites/create?type=2');
              }}
            >
              {translate('buttons.invite')}
            </CreateButton>
          </Space>
        ),
      }}
    >
      <Form {...formProps}>
        <Table {...tableProps} rowKey="_id" data-testid="practice-users-list">
          <Table.Column key="_key" dataIndex="_key" title="ID" />
          <Table.Column<User>
            key="firstName"
            dataIndex="firstName"
            title={translate('practiceUsers.fields.firstName')}
            sorter
            render={(value, record) => {
              if (isEditing(record._id)) {
                return (
                  <Form.Item name={['firstName']}>
                    <Input />
                  </Form.Item>
                );
              }
              return value;
            }}
          />
          <Table.Column<User>
            key="lastName"
            dataIndex="lastName"
            title={translate('practiceUsers.fields.lastName')}
            sorter
            render={(value, record) => {
              if (isEditing(record._id)) {
                return (
                  <Form.Item name={['lastName']}>
                    <Input />
                  </Form.Item>
                );
              }
              return value;
            }}
          />
          <Table.Column<User>
            key="role"
            dataIndex="role"
            title={translate('practiceUsers.fields.role')}
            render={(value, record) => {
              if (record._id !== identity?.db?._id && isEditing(record._id)) {
                return (
                  <Form.Item name={['role']}>
                    <Select
                      style={{ width: 250 }}
                      options={[
                        {
                          value: UserRole.THERAPIST_ADMIN,
                          label: translate(`roles.${UserRole.THERAPIST_ADMIN}`),
                        },
                        {
                          value: UserRole.THERAPIST,
                          label: translate(`roles.${UserRole.THERAPIST}`),
                        },
                      ]}
                    />
                  </Form.Item>
                );
              }
              return translate(`roles.${value}`);
            }}
            sorter
          />
          <Table.Column
            key="email"
            dataIndex="email"
            title={translate('practiceUsers.fields.email')}
          />
          <Table.Column<User>
            title={translate('resourcesCommon.actions')}
            dataIndex="actions"
            render={(_, record) => {
              if (isEditing(record._id)) {
                return (
                  <Space>
                    <SaveButton {...saveButtonProps} size="small" />
                    <Button {...cancelButtonProps} size="small">
                      {translate('buttons.cancel')}
                    </Button>
                  </Space>
                );
              }

              return (
                <Space>
                  <EditButton
                    {...editButtonProps(record._id)}
                    hideText
                    size="small"
                    data-testid="cc-user-list-edit-button"
                  />
                  {record._id !== identity?.db?._id && (
                    <DeleteButton
                      hideText
                      size="small"
                      recordItemId={record._id}
                      meta={{
                        fields: ['_id'],
                      }}
                    />
                  )}
                </Space>
              );
            }}
          />
        </Table>
      </Form>
    </List>
  );
};
